<template>
  <div class="line-item-container">
    <!-- Search Section -->
    <div class="search-section">
      <div class="search-header">
        <h2 class="section-title">Order New Part</h2>
        <div class="search-form">
          <div class="form-group">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Enter customer part number"
              class="form-input"
              @keyup.enter="searchItems"
            />
          </div>
          <div v-if="isAdminOrManager" class="form-group">
            <select 
              v-model="selectedCustomer"
              class="form-input customer-select"
            >
              <option value="">All Customers</option>
              <option v-for="customer in customers" :key="customer" :value="customer">
                {{ customer }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Results Section -->
    <div v-if="searchPerformed && !hideSearchResults" class="results-section">
      <h3 class="section-subtitle">Search Results</h3>
      <div v-if="isSearching" class="loading-message">
        Searching for items...
      </div>
      <div v-else-if="items.length === 0" class="no-results">
        No items found for the given search criteria.
      </div>
      <div v-else class="results-table">
        <table class="table">
          <thead>
            <tr>
              <th>Customer Part</th>
              <th>Last Modified</th>
              <th>Description</th>
              <th>Hose</th>
              <th>Overall Length</th>
              <th>Fitting 1</th>
              <th>Fitting 2</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.CustPart }}</td>
              <td>{{ formatDate(item.LastModDate) }}</td>
              <td>{{ item.Description || '-' }}</td>
              <td>{{ item.Hose }}</td>
              <td>
                <input 
                  type="text" 
                  :value="item.Length" 
                  class="table-input"
                  readonly
                />
              </td>
              <td>{{ item.FittingA }}</td>
              <td>{{ item.FittingB }}</td>
              <td>
                <span v-if="getItemDisplayUnitPrice(item) !== null">€{{ formatPrice(getItemDisplayUnitPrice(item)) }}</span>
                <span v-else>-</span>
                <div v-if="item.PriceWarning" class="price-warning" style="font-size:11px;color:#c05600;">{{ item.PriceWarning }}</div>
              </td>
              <td>
                <div class="action-buttons">
                  <button 
                    v-if="getItemDisplayUnitPrice(item) === null"
                    @click="calculateItemCostFromSearch(item)"
                    class="calculate-price-button btn-icon"
                    title="Calculate Price"
                  >
                    €
                  </button>
                  <button 
                    class="btn btn-primary btn-sm"
                    @click="addToOrder(item)"
                    :disabled="isOrderItem(item)"
                  >
                    {{ isOrderItem(item) ? 'Added' : 'Add' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Current Order Section -->
    <div v-if="orderItems.length > 0" class="order-section">
      <div class="order-header">
        <button 
          @click="toggleSearchResults" 
          class="toggle-search-button"
        >
          {{ hideSearchResults ? 'Show Search Results' : 'Hide Search Results' }}
        </button>
        <h3 class="section-subtitle">Current Order</h3>
      </div>
      <div class="results-table">
        <table>
          <thead>
            <tr>
              <th>Customer Part #</th>
              <th>Hose</th>
              <th>Overall Length</th>
              <th>Fittings</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in orderItems" :key="item.LineID">
              <td>{{ item.CustPart }}</td>
              <td>{{ item.Hose }}</td>
              <td>
                <input 
                  type="text" 
                  :value="item.Length" 
                  class="table-input"
                  readonly
                />
              </td>
              <td>
                <div class="fitting-preview">
                  A: {{ item.FittingA }} / B: {{ item.FittingB }}
                  <button 
                    @click="editItemDetails(index)" 
                    class="edit-details-button"
                  >
                    Details
                  </button>
                </div>
              </td>
              <td>
                <input 
                  type="number" 
                  v-model="item.Quantity" 
                  min="1" 
                  class="quantity-input"
                />
              </td>
              <td>
                <span v-if="getItemDisplayTotal(item) !== null">€{{ formatPrice(getItemDisplayTotal(item)) }}</span>
                <span v-else>-</span>
                <div v-if="item.PriceWarning" class="price-warning" style="font-size:11px;color:#c05600;">Local price used (IQ low)</div>
              </td>
              <td>
                <button 
                  v-if="getItemDisplayTotal(item) === null"
                  @click="calculateItemCost(index)" 
                  class="calculate-price-button"
                >
                  Calculate Price
                </button>
                <button 
                  @click="removeFromOrder(index)" 
                  class="remove-button"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="order-actions">
          <div class="order-info">
            <div class="form-row">
              <div class="form-group">
                <label for="orderNumber">Order Number</label>
                <input 
                  type="text" 
                  v-model="orderNumber" 
                  id="orderNumber"
                  class="form-input"
                  placeholder="Enter order number"
                  required
                />
              </div>
              <div class="form-group">
                <label for="deliveryMethod">Delivery Method</label>
                <select id="deliveryMethod" v-model="shippingMethod" class="form-input">
                  <option value="SPARES">SPARES</option>
                  <option value="DHL">DHL</option>
                </select>
              </div>
            </div>
            <div class="form-group" v-if="shippingMethod === 'DHL'">
              <label for="shippingLabel">Shipping Label (PDF)</label>
              <input 
                type="file" 
                id="shippingLabel"
                accept=".pdf"
                @change="handleFileUpload($event, 'shippingLabel')"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="picklist">PickList (PDF)</label>
              <input 
                type="file" 
                id="picklist"
                accept=".pdf"
                @change="handleFileUpload($event, 'picklist')"
                class="form-input"
              />
            </div>
          </div>
          <button 
            @click="createOrder" 
            class="create-order-button"
            :disabled="isCreatingOrder || !orderNumber.trim()"
          >
            {{ isCreatingOrder ? 'Creating Order...' : 'Create Order' }}
          </button>
        </div>
      </div>

      <!-- Progress Modal -->
      <div v-if="orderProgress.show" class="modal-overlay">
        <div class="progress-modal">
                     <div class="progress-header">
             <div class="progress-header-top">
               <h3 class="progress-title">Creating Order</h3>
               <button 
                 @click="closeProgressModal" 
                 class="progress-close-button"
                 :disabled="orderProgress.currentStep < orderProgress.totalSteps && !orderProgress.error && !hasStepErrors"
                 title="Close progress dialog"
               >
                 ✕
               </button>
             </div>
             <div class="progress-bar-container">
               <div class="progress-bar">
                 <div 
                   class="progress-fill" 
                   :style="{ width: (orderProgress.currentStep / orderProgress.totalSteps * 100) + '%' }"
                 ></div>
               </div>
               <span class="progress-text">
                 {{ orderProgress.currentStep }} of {{ orderProgress.totalSteps }} steps
               </span>
             </div>
           </div>
          
          <div class="progress-steps">
            <div 
              v-for="(step, index) in orderProgress.steps" 
              :key="index"
              class="progress-step"
              :class="{ 
                'active': index === orderProgress.currentStep,
                'completed': step.status === 'completed',
                'error': step.status === 'error',
                'processing': step.status === 'processing'
              }"
            >
              <div class="step-icon">
                <span v-if="step.status === 'completed'" class="icon-check">✓</span>
                <span v-else-if="step.status === 'error'" class="icon-error">✗</span>
                <span v-else-if="step.status === 'processing'" class="icon-spinner">⟳</span>
                <span v-else class="step-number">{{ index + 1 }}</span>
              </div>
              <div class="step-content">
                <div class="step-name">{{ step.name }}</div>
                <div v-if="step.message" class="step-message">{{ step.message }}</div>
              </div>
            </div>
          </div>

                     <div v-if="orderProgress.error" class="progress-error">
             <h4>Error occurred:</h4>
             <p>{{ orderProgress.error }}</p>
             <button @click="closeProgressModal" class="btn btn-primary">Close</button>
           </div>
           
           <div v-if="orderProgress.currentStep >= orderProgress.totalSteps && !orderProgress.error" class="progress-success">
             <div class="success-icon">✓</div>
             <h4>Order Created Successfully!</h4>
             <p v-if="!hasStepErrors">Your order has been processed and is now in the system.</p>
             <p v-else>Your order has been created, but some steps had issues. Check the details above.</p>
             <button @click="closeProgressModal" class="btn btn-primary">Close</button>
           </div>
           
           <div v-if="hasStepErrors && !orderProgress.error && orderProgress.currentStep < orderProgress.totalSteps" class="progress-partial-error">
             <h4>Issues Detected</h4>
             <p>Some steps have encountered problems. You can close this dialog or wait for the remaining steps to complete.</p>
             <button @click="closeProgressModal" class="btn btn-primary">Close</button>
           </div>
         </div>
       </div>

      <!-- Item Details Modal -->
      <div v-if="editingItemIndex !== null" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title">Item Details</h3>
            <button class="close-button" @click="closeItemDetails">&times;</button>
          </div>
          <form @submit.prevent="saveItemDetails" class="edit-form">
            <!-- Fitting A Details -->
            <div class="form-section">
              <h4 class="form-section-title">Fitting A Details</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Main Fitting (Coupling)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingA" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Fitting A1 (Adaptor)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingA1" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Fitting A2 (Adaptor)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingA2" 
                    class="form-input"
                    readonly
                  />
                </div>
              </div>
            </div>

            <!-- Fitting B Details -->
            <div class="form-section">
              <h4 class="form-section-title">Fitting B Details</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Fitting B (Coupling)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingB" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Fitting B1 (Adaptor)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingB1" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Fitting B2 (Adaptor)</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].FittingB2" 
                    class="form-input"
                    readonly
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Orientation</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].Orientation" 
                    class="form-input"
                    readonly
                  />
                </div>
              </div>
            </div>

            <!-- Additional Details -->
            <div class="form-section">
              <h4 class="form-section-title">Additional Details</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Hose</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].Hose" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Overall Length</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].Length" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>Cut Length</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].CutLength" 
                    class="form-input"
                    readonly
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group checkbox-group">
                  <label class="checkbox-label">
                    <input 
                      type="checkbox" 
                      :checked="hasVpcData(orderItems[editingItemIndex])"
                      disabled
                    /> VPC
                  </label>
                </div>
              </div>
              <div v-if="hasVpcData(orderItems[editingItemIndex])" class="form-row">
                <div class="form-group">
                  <label>VPC Part</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].VPCPart" 
                    class="form-input"
                    readonly
                  />
                </div>
                <div class="form-group">
                  <label>VPC Length</label>
                  <input 
                    type="text" 
                    :value="orderItems[editingItemIndex].VPCLen" 
                    class="form-input"
                    readonly
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Note</label>
                  <textarea 
                    v-model="orderItems[editingItemIndex].Note" 
                    class="form-input"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button 
                type="button" 
                @click="closeItemDetails" 
                class="cancel-button"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Edit Form Section -->
    <div v-if="selectedItem" class="edit-section">
      <h3 class="section-subtitle">Create New Item</h3>
      <form @submit.prevent="saveNewItem" class="edit-form">
        <!-- Basic Information -->
        <div class="form-section">
          <h4 class="form-section-title">Basic Information</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="kitid">Kit ID</label>
              <input 
                type="number" 
                v-model="newItem.kitid" 
                id="kitid" 
                required
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="custpart">Customer Part Number</label>
              <input 
                type="text" 
                v-model="newItem.custpart" 
                id="custpart" 
                required
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="billpart">Bill Part Number</label>
              <input 
                type="text" 
                v-model="newItem.BillPart" 
                id="billpart"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Hose Details -->
        <div class="form-section">
          <h4 class="form-section-title">Hose Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="hose">Hose Type</label>
              <input 
                type="text" 
                v-model="newItem.hose" 
                id="hose"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="length">Length</label>
              <input 
                type="number" 
                v-model="newItem.length" 
                id="length"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="cutlength">Cut Length</label>
              <input 
                type="number" 
                v-model="newItem.cutlength" 
                id="cutlength"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Fitting A Details -->
        <div class="form-section">
          <h4 class="form-section-title">Fitting A Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="fittinga">Main Fitting</label>
              <input 
                type="text" 
                v-model="newItem.fittinga" 
                id="fittinga"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittinga1">Fitting A1</label>
              <input 
                type="text" 
                v-model="newItem.fittinga1" 
                id="fittinga1"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittinga2">Fitting A2</label>
              <input 
                type="text" 
                v-model="newItem.fittinga2" 
                id="fittinga2"
                class="form-input"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="fittinga3">Fitting A3</label>
              <input 
                type="text" 
                v-model="newItem.fittinga3" 
                id="fittinga3"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittinga4">Fitting A4</label>
              <input 
                type="text" 
                v-model="newItem.fittinga4" 
                id="fittinga4"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittinga5">Fitting A5</label>
              <input 
                type="text" 
                v-model="newItem.fittinga5" 
                id="fittinga5"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Fitting B Details -->
        <div class="form-section">
          <h4 class="form-section-title">Fitting B Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="fittingb">Main Fitting</label>
              <input 
                type="text" 
                v-model="newItem.fittingb" 
                id="fittingb"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittingb1">Fitting B1</label>
              <input 
                type="text" 
                v-model="newItem.fittingb1" 
                id="fittingb1"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittingb2">Fitting B2</label>
              <input 
                type="text" 
                v-model="newItem.fittingb2" 
                id="fittingb2"
                class="form-input"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="fittingb3">Fitting B3</label>
              <input 
                type="text" 
                v-model="newItem.fittingb3" 
                id="fittingb3"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittingb4">Fitting B4</label>
              <input 
                type="text" 
                v-model="newItem.fittingb4" 
                id="fittingb4"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fittingb5">Fitting B5</label>
              <input 
                type="text" 
                v-model="newItem.fittingb5" 
                id="fittingb5"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Additional Details -->
        <div class="form-section">
          <h4 class="form-section-title">Additional Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="orientation">Orientation</label>
              <input 
                type="text" 
                v-model="newItem.orientation" 
                id="orientation"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="quantity">Quantity</label>
              <input 
                type="number" 
                v-model="newItem.quantity" 
                id="quantity"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="sequence">Sequence</label>
              <input 
                type="number" 
                v-model="newItem.sequence" 
                id="sequence"
                class="form-input"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="bin">Bin</label>
              <input 
                type="number" 
                v-model="newItem.bin" 
                id="bin"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="stocklevel">Stock Level</label>
              <input 
                type="number" 
                v-model="newItem.stocklevel" 
                id="stocklevel"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="stockdate">Stock Date</label>
              <input 
                type="datetime-local" 
                v-model="newItem.stockdate" 
                id="stockdate"
                class="form-input"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="newItem.flush"
                /> Flush
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="newItem.vpc"
                /> VPC
              </label>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="vpcpart">VPC Part</label>
              <input 
                type="text" 
                v-model="newItem.vpcpart" 
                id="vpcpart"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="vpclen">VPC Length</label>
              <input 
                type="number" 
                v-model="newItem.vpclen" 
                id="vpclen"
                class="form-input"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="note">Note</label>
            <textarea 
              v-model="newItem.note" 
              id="note"
              rows="3"
              class="form-input"
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button 
            type="button" 
            @click="cancelEdit" 
            class="cancel-button"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            class="save-button"
            :disabled="isSaving"
          >
            {{ isSaving ? 'Saving...' : 'Save as New Item' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cost Calculation Modal -->
    <div v-if="showCostModal" class="modal-overlay">
      <div class="modal-content cost-modal">
        <div class="modal-header">
          <h3 v-if="isAdminOrManager">Cost Breakdown - {{ costModalPart || '-' }}</h3>
          <h3 v-else>{{ costModalPart || '-' }}</h3>
          <div class="modal-header-right">
            <span v-if="!isAdminOrManager">Qty: {{ costModalQty || 1 }}</span>
            <button @click="showCostModal = false" class="close-button">&times;</button>
          </div>
        </div>
        <div class="modal-body">
          <div v-if="selectedItemCost" class="cost-breakdown">
            <div class="cost-items" v-if="isAdminOrManager">
              <div class="cost-item">
                <span class="label">Hose Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.hoseCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">VPC Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.vpcCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">Fittings Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.fittingsCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">Labels Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.labelsCost).toFixed(2) }}</span>
              </div>
            </div>
            <div class="cost-total">
              <div class="total-divider"></div>
              <div class="total-row">
                <span class="total-label">Total Price</span>
                <span class="total-value">€{{ Number(selectedItemCost.totalCost).toFixed(2) }}</span>
              </div>
            </div>
          </div>
          <div v-else class="loading-cost">
            <div class="loading-spinner"></div>
            <span>Calculating costs...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LineItemSearchView',
  data() {
    return {
      searchQuery: '',
      items: [],
      orderItems: [],
      selectedItem: null,
      orderNumber: '',
      shippingMethod: 'SPARES',
      selectedCustomer: '',
      customers: [],
      userRole: '',
      newItem: {
        kitid: null,
        custpart: '',
        length: null,
        cutlength: null,
        fittinga: '',
        fittingb: '',
        orientation: '',
        hose: '',
        flush: false,
        quantity: 1,
        vpc: false,
        item: null,
        vpclen: null,
        note: '',
        sequence: null,
        vpcpart: '',
        stockdate: null,
        stocklevel: 0,
        bin: null,
        BillPart: '',
        fittinga1: '',
        fittinga2: '',
        fittinga3: '',
        fittinga4: '',
        fittinga5: '',
        fittingb1: '',
        fittingb2: '',
        fittingb3: '',
        fittingb4: '',
        fittingb5: '',
      },
      editingItemIndex: null,
      isSearching: false,
      isCreatingOrder: false,
      searchPerformed: false,
      error: null,
      hideSearchResults: false,
      shippingLabelFile: null,
      picklistFile: null,
      selectedHose: null,
      availableHoses: [],
      availableAdaptors: [],
      availableCrimps: [],
      showCostModal: false,
      selectedItemCost: null,
      costModalPart: null,
      costModalQty: 1,
      // Progress tracking
      orderProgress: {
        show: false,
        currentStep: 0,
        totalSteps: 6,
        steps: [
          { name: 'Validating User', status: 'pending', message: '' },
          { name: 'Processing Products', status: 'pending', message: '' },
          { name: 'Creating Order', status: 'pending', message: '' },
          { name: 'Adding Line Items', status: 'pending', message: '' },
          { name: 'Calculating Prices', status: 'pending', message: '' },
          { name: 'Creating Sales Order', status: 'pending', message: '' }
        ],
        error: null
      },
      // Caching flags to prevent duplicate API calls
      dataLoaded: {
        hoses: false,
        adaptors: false,
        crimps: false
      },
      // IQ price cache by CustPart (unit price)
      iqPrices: {},
    };
  },
  methods: {
    async fetchCustomers() {
      if (!this.isAdminOrManager) {
        return;
      }
      try {
        const response = await axios.get('/customers');
        this.customers = response.data.data;
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    },

    async searchItems() {
      this.isSearching = true;
      this.error = null;
      this.hideSearchResults = false;
      this.searchPerformed = true;

      try {
        const response = await axios.get('/lineitems/search', {
          params: {
            custpart: this.searchQuery,
            customer: this.selectedCustomer,
            status: this.selectedStatus,
            page: this.currentPage,
            limit: this.itemsPerPage
          }
        });
        this.items = response.data;
        this.totalItems = response.data.length;
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        console.log('Full search response.data:', response.data);
        console.log('Number of items returned:', response.data.length);
        if (response.data.length > 0) {
          console.log('First item keys:', Object.keys(response.data[0]));
          console.log('First item BillPart field:', response.data[0].BillPart);
          console.log('Search results with BillPart:', response.data.map(item => ({ CustPart: item.CustPart, BillPart: item.BillPart })));
        }
        // Load IQ prices for search results
        await this.loadIqPricesForItems(this.items);
      } catch (error) {
        console.error('Error searching line items:', error);
        this.error = error.response?.data?.message || 'Failed to search line items';
      } finally {
        this.isSearching = false;
      }
    },

    async loadIqPricesForItems(items) {
      try {
        const productCodesRaw = (items || []).map(i => i.CustPart).filter(Boolean);
        if (productCodesRaw.length === 0) return;
        // Deduplicate and skip codes already in cache
        const unique = Array.from(new Set(productCodesRaw));
        const productCodes = unique.filter(code => this.iqPrices[code] === undefined);
        if (productCodes.length === 0) return;
        const body = { productCodes };
        if (this.isAdminOrManager && this.selectedCustomer) {
          body.customer = this.selectedCustomer;
        }
        const resp = await axios.post('/product-validation/batch', body);
        const results = resp.data?.results || [];
        const priceMap = { ...this.iqPrices };
        for (const r of results) {
          if (r && r.success && r.exists) {
            let data = r.data;
            if (data && Array.isArray(data)) data = data[0];
            if (data && data.Data && Array.isArray(data.Data)) data = data.Data[0];
            const price = Number(data?.SellingPrice1 ?? data?.Selling?.SellingPrice1);
            if (!isNaN(price) && price > 0) {
              const key = r.productCode || data?.Code || null;
              if (key) priceMap[key] = price;
            }
          }
        }
        this.iqPrices = priceMap;
      } catch (e) {
        console.warn('Failed to load IQ prices:', e.message);
      }
    },

    getIqPrice(custPart) {
      if (!custPart) return null;
      return this.iqPrices[custPart] ?? null;
    },

    /** Prefer resolved display unit price on order items; else IQ; else LocalPrice as unit. */
    getItemDisplayUnitPrice(item) {
      if (!item) return null;
      if (item.DisplayUnitPrice !== undefined && item.DisplayUnitPrice !== null && !isNaN(Number(item.DisplayUnitPrice))) {
        return Number(item.DisplayUnitPrice);
      }
      const iq = this.getIqPrice(item.CustPart);
      if (iq !== null && iq !== undefined && !isNaN(Number(iq))) {
        return Number(iq);
      }
      if (item.LocalPrice !== undefined && item.LocalPrice !== null && !isNaN(Number(item.LocalPrice))) {
        const qty = Math.max(1, Number(item.Quantity) || 1);
        return Number(item.LocalPrice) / qty;
      }
      return null;
    },

    getItemDisplayTotal(item) {
      if (!item) return null;
      if (item.DisplayTotal !== undefined && item.DisplayTotal !== null && !isNaN(Number(item.DisplayTotal))) {
        const storedQty = Math.max(1, Number(item.PriceResolvedQty) || 1);
        const currentQty = Math.max(1, Number(item.Quantity) || 1);
        if (item.DisplayUnitPrice != null && currentQty !== storedQty) {
          return Number((Number(item.DisplayUnitPrice) * currentQty).toFixed(2));
        }
        if (currentQty === storedQty) return Number(item.DisplayTotal);
      }
      const unit = this.getItemDisplayUnitPrice(item);
      if (unit === null) return null;
      const qty = Math.max(1, Number(item.Quantity) || 1);
      return Number((unit * qty).toFixed(2));
    },

    formatPrice(value) {
      const n = Number(value);
      if (isNaN(n)) return '-';
      return n.toFixed(2);
    },

    hasVpcData(item) {
      if (!item) return false;
      const vpcLen = Number(item.VPCLen);
      return !!item.VPCPart && !isNaN(vpcLen) && vpcLen > 0;
    },

    buildCostCalcBody(item, kitDetails) {
      const labelValue = (kitDetails && kitDetails.Label) ?? item.KitDetails?.Label ?? false;
      const lblDoubles = (kitDetails && kitDetails.LblDoubles) ?? item.KitDetails?.LblDoubles ?? "2000";
      const customerValue = this.isAdminOrManager ? this.selectedCustomer : this.$store.state.user?.customer;
      const vpcLen = Number(item.VPCLen);
      const hasVpcData = !!item.VPCPart && !isNaN(vpcLen) && vpcLen > 0;
      return {
        CustPart: item.CustPart,
        Hose: item.Hose,
        CutLength: item.CutLength,
        VPCPart: hasVpcData ? item.VPCPart : null,
        VPCLen: hasVpcData ? item.VPCLen : null,
        FittingA: item.FittingA,
        FittingB: item.FittingB,
        FittingA1: item.FittingA1,
        FittingA2: item.FittingA2,
        FittingA3: item.FittingA3,
        FittingA4: item.FittingA4,
        FittingA5: item.FittingA5,
        FittingB1: item.FittingB1,
        FittingB2: item.FittingB2,
        FittingB3: item.FittingB3,
        FittingB4: item.FittingB4,
        FittingB5: item.FittingB5,
        Label: labelValue,
        LblDoubles: lblDoubles,
        Quantity: item.Quantity || 1,
        customer: customerValue
      };
    },

    async resolveItemDisplayPrice(item, kitDetails) {
      const body = this.buildCostCalcBody(item, kitDetails);
      const resp = await axios.post('/cost-calculation/resolve-display-price', body);
      if (!resp.data?.success || !resp.data?.data) {
        throw new Error(resp.data?.message || 'Price validation failed');
      }
      return resp.data.data;
    },
    
    async addToOrder(item) {
      // Check if the item is already in the order
      const existingItem = this.orderItems.find(i => i.LineID === item.LineID);
      if (existingItem) {
        alert('This item is already in the order');
        return;
      }

      try {
        // Find the matching hose from availableHoses
        const defaultHose = this.availableHoses.find(hose => 
          hose.PartNo === item.Hose || 
          hose.Description === item.Hose
        );

        // If we have a KitID, fetch the kit details
        let kitDetails = item.KitDetails;
        if (item.KitID && !kitDetails) {
          console.log('Fetching kit details for KitID:', item.KitID);

          let url = '/kits';
          
          const params = new URLSearchParams();
          params.append('kitid', item.KitID);
          if (this.isAdminOrManager && this.selectedCustomer) {
            params.append('customer', this.selectedCustomer);
          }
          
          if (params.toString()) {
            url += `?${params.toString()}`;
          }

          const response = await axios.get(url);

          console.log('Kit response:', response.data);
          
          if (response.data && response.data.data && response.data.data.length > 0) {
            const kit = response.data.data[0];
            kitDetails = {
              Label: kit.Label ?? false,
              LblDoubles: kit.LblDoubles ?? "2000",
              LblJobNumber: kit.LblJobNumber ?? false
            };
            console.log('Found kit details:', kitDetails);
          } else {
            console.log('No kit details found, using defaults');
          }
        }

        const hosePart = defaultHose ? defaultHose.PartNo : item.Hose;
        const itemForPrice = { ...item, Hose: hosePart };

        // Validate IQ vs local before adding; block zeros and override IQ if >10% below local
        let priceResolved = null;
        try {
          priceResolved = await this.resolveItemDisplayPrice(itemForPrice, kitDetails);
        } catch (priceErr) {
          console.error('Price resolve failed:', priceErr);
          alert('Unable to validate price for this item. It was not added to the order.');
          return;
        }

        if (priceResolved.blocked) {
          alert(priceResolved.blockReason || 'Price is zero — this item cannot be added to an order. Admins have been notified.');
          return;
        }

        if (priceResolved.warning) {
          console.warn(priceResolved.warning);
        }

        // Keep search-list IQ cache in sync with display unit when overridden
        if (item.CustPart && priceResolved.displayUnitPrice != null) {
          this.iqPrices = {
            ...this.iqPrices,
            [item.CustPart]: priceResolved.displayUnitPrice
          };
        }

        console.log('Adding item to order with KitDetails:', kitDetails);
        console.log('Price resolve:', priceResolved);

        this.orderItems.push({
          ...item,
          Hose: hosePart,
          editing: false,
          KitDetails: kitDetails || {
            Label: false,
            LblDoubles: "2000",
            LblJobNumber: false
          },
          LocalPrice: priceResolved.localTotal,
          LocalUnitPrice: priceResolved.localUnitPrice,
          DisplayUnitPrice: priceResolved.displayUnitPrice,
          DisplayTotal: priceResolved.displayTotal,
          PriceSource: priceResolved.priceSource,
          PriceWarning: priceResolved.warning || null,
          PriceResolvedQty: Math.max(1, Number(item.Quantity) || 1),
          IqUnitPrice: priceResolved.iqUnitPrice
        });

        // Hide the search results
        this.hideSearchResults = true;
      } catch (error) {
        console.error('Error adding item to order:', error);
        this.error = 'Failed to add item to order. Please try again.';
      }
    },

    removeFromOrder(index) {
      this.orderItems.splice(index, 1);
    },
    
    async createOrder() {
      if (this.orderItems.length === 0 || !this.orderNumber.trim()) {
        this.error = 'Please enter an order number and add items to the order.';
        return;
      }

      // Validate customer selection for admin/manager
      if (this.isAdminOrManager && !this.selectedCustomer) {
        this.error = 'Please select a customer before creating the order.';
        return;
      }

      // Re-validate prices before order (block zeros; refresh display totals)
      try {
        for (let i = 0; i < this.orderItems.length; i++) {
          const item = this.orderItems[i];
          const resolved = await this.resolveItemDisplayPrice(item, item.KitDetails);
          if (resolved.blocked) {
            this.error = `${item.CustPart}: ${resolved.blockReason || 'Price is zero — cannot create order. Admins have been notified.'}`;
            return;
          }
          this.orderItems[i] = {
            ...item,
            LocalPrice: resolved.localTotal,
            LocalUnitPrice: resolved.localUnitPrice,
            DisplayUnitPrice: resolved.displayUnitPrice,
            DisplayTotal: resolved.displayTotal,
            PriceSource: resolved.priceSource,
            PriceWarning: resolved.warning || null,
            PriceResolvedQty: Math.max(1, Number(item.Quantity) || 1),
            IqUnitPrice: resolved.iqUnitPrice
          };
          if (item.CustPart && resolved.displayUnitPrice != null) {
            this.iqPrices = { ...this.iqPrices, [item.CustPart]: resolved.displayUnitPrice };
          }
        }
      } catch (prePriceErr) {
        console.error('Pre-order price validation failed:', prePriceErr);
        this.error = 'Unable to validate item prices. Order was not created.';
        return;
      }
      
      this.isCreatingOrder = true;
      this.error = null;
      
      // Initialize progress
      this.initializeProgress();
      
      try {
        // Step 1: Validate user
        this.setStepProcessing(0, 'Checking user authentication...');
        const authResponse = await axios.get('/auth/check-auth');
        const currentUser = authResponse.data.user.email;
        const userCustomer = this.isAdminOrManager ? this.selectedCustomer : authResponse.data.user.customer;

        if (!currentUser || !userCustomer) {
          throw new Error('Could not retrieve user information');
        }
        this.setStepCompleted(0, 'User validated successfully');

        // Step 2: Process products
        this.setStepProcessing(1, 'Validating and creating products...');
        const validOrderItems = this.orderItems.filter(item => item.CustPart && item.CustPart.trim());
        
        if (validOrderItems.length > 0) {
          try {
            const validationResponse = await axios.post('/product-validation/validate-or-create-batch', {
              orderItems: validOrderItems,
              customer: userCustomer  // Pass the target customer for prefix lookup
            });
            
            console.log('Product validation/creation results:', validationResponse.data);
            
            if (validationResponse.data.results) {
              const failedProducts = validationResponse.data.results.filter(result => !result.success);
              const createdProducts = validationResponse.data.results.filter(result => result.action === 'created');
              const validatedProducts = validationResponse.data.results.filter(result => result.action === 'validated');
              
              if (failedProducts.length > 0) {
                const failedCodes = failedProducts.map(p => `${p.productCode} (${p.message})`).join(', ');
                this.setStepError(1, `Failed to process products: ${failedCodes}`);
                throw new Error(`Failed to process the following products: ${failedCodes}. Please check the product data and try again.`);
              }
              
              const statusMessage = `${validatedProducts.length} validated, ${createdProducts.length} created`;
              this.setStepCompleted(1, statusMessage);
              
              if (createdProducts.length > 0) {
                const createdCodes = createdProducts.map(p => p.productCode).join(', ');
                console.log(`Created new products: ${createdCodes}`);
              }
              
              if (validatedProducts.length > 0) {
                const validatedCodes = validatedProducts.map(p => p.productCode).join(', ');
                console.log(`Validated existing products: ${validatedCodes}`);
              }
            } else {
              this.setStepCompleted(1, 'Products processed successfully');
            }
          } catch (validationError) {
            console.warn('Product validation/creation failed:', validationError);
            this.setStepError(1, 'Product validation failed - continuing anyway');
            // Continue with order creation even if validation fails
          }
        } else {
          this.setStepCompleted(1, 'No products to validate');
        }

        // Step 3: Create order
        this.setStepProcessing(2, 'Creating web order...');
        const formData = new FormData();
        formData.append('Description', this.orderItems[0]?.KitDetails?.Description || `WebOrder_${new Date().toLocaleDateString()}`);
        formData.append('CreateDate', new Date().toISOString());
        formData.append('UserName', currentUser);
        formData.append('Customer', userCustomer);
        formData.append('Instructions', this.orderItems[0]?.KitDetails?.Instructions || '');
        formData.append('LblDateCode', this.orderItems[0]?.KitDetails?.LblDateCode || true);
        formData.append('LblDoubles', this.orderItems[0]?.KitDetails?.LblDoubles || '2000');
        formData.append('Label', this.orderItems[0]?.KitDetails?.Label || true);
        formData.append('LblJobNumber', this.orderItems[0]?.KitDetails?.LblJobNumber || false);
        const poWithMethod = `${this.orderNumber} - ${this.shippingMethod}`;
        formData.append('OrderNumber', poWithMethod);
        
        if (this.shippingLabelFile) {
          formData.append('shippingLabel', this.shippingLabelFile);
        }
        if (this.picklistFile) {
          formData.append('picklist', this.picklistFile);
        }

        const kitResponse = await axios.post('/weborders', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('web order creation response:', kitResponse);
        
        if (!kitResponse.data || typeof kitResponse.data !== 'object') {
          throw new Error('Invalid response from order creation');
        }

        const kitId = kitResponse.data.id;
        if (!kitId && kitId !== 0) {
          throw new Error('No kitid returned from order creation. Response: ' + JSON.stringify(kitResponse.data));
        }

        this.setStepCompleted(2, `Order created with ID: ${kitId}`);
        console.log('Using kit ID:', kitId);

        // Step 4: Add line items
        this.setStepProcessing(3, `Adding ${this.orderItems.length} line items...`);
        let addedItems = 0;
        
        for (const item of this.orderItems) {
          // Prefer validated display total (IQ vs local with 10% rule)
          const displayTotal = this.getItemDisplayTotal(item);
          const quantityForTotal = Number(item.Quantity || 1);
          const fallbackIqUnit = this.getIqPrice(item.CustPart);
          const fallbackIqTotal = (fallbackIqUnit !== null && !isNaN(fallbackIqUnit) && fallbackIqUnit > 0)
            ? Number((fallbackIqUnit * quantityForTotal).toFixed(2))
            : null;
          const localTotal = (item.LocalPrice !== undefined && item.LocalPrice !== null)
            ? Number(item.LocalPrice)
            : null;

          const netPrice = (displayTotal !== null && displayTotal > 0)
            ? displayTotal
            : (fallbackIqTotal !== null ? fallbackIqTotal : (localTotal !== null ? localTotal : undefined));

          if (!netPrice || netPrice <= 0) {
            throw new Error(`${item.CustPart}: price is zero — cannot add to order`);
          }

          const lineItemData = {
            ...item,
            WebOrderID: kitId,  // Use the WebOrderID from the response
            Customer: userCustomer, // Add the customer to each line item
            // Ensure all required fields are present
            CustPart: item.CustPart || '',
            Length: item.Length || 0,
            CutLength: item.CutLength || item.Length || 0,
            FittingA: item.FittingA || '',
            FittingB: item.FittingB || '',
            Orientation: item.Orientation || '',
            Hose: item.Hose || '',
            Quantity: item.Quantity || 1,
            VPC: item.VPC || false,
            VPCLen: item.VPCLen || null,
            Note: item.Note || '',
            VPCPart: item.VPCPart || '',
            BillPart: item.BillPart || item.billpart || item.Billpart || item.CustPart || '',
            FittingA1: item.FittingA1 || '',
            FittingA2: item.FittingA2 || '',
            FittingA3: item.FittingA3 || '',
            FittingA4: item.FittingA4 || '',
            FittingA5: item.FittingA5 || '',
            FittingB1: item.FittingB1 || '',
            FittingB2: item.FittingB2 || '',
            FittingB3: item.FittingB3 || '',
            FittingB4: item.FittingB4 || '',
            FittingB5: item.FittingB5 || '',
            NetPrice: netPrice
          };

          const lineItemResponse = await axios.post('/weborderlineitems', lineItemData);
          console.log('Line item created:', lineItemResponse.data);
          console.log('Sent BillPart value:', lineItemData.BillPart);
          
          addedItems++;
          this.setStepProcessing(3, `Added ${addedItems} of ${this.orderItems.length} line items...`);
        }
        
        this.setStepCompleted(3, `All ${addedItems} line items added successfully`);

        // Step 5: Calculate prices for line items
        this.setStepProcessing(4, 'Calculating prices for line items...');
        
        try {
          // Get all line items that were just created to calculate their IDs
          const lineItemsResponse = await axios.get(`/weborderlineitems/${kitId}`);
          const createdLineItems = lineItemsResponse.data;
          
          console.log(`Found ${createdLineItems.length} line items to calculate prices for`);
          
          // Ensure IQ prices are loaded for the created items
          await this.loadIqPricesForItems(createdLineItems);
          
          let processedCount = 0;
          for (const lineItem of createdLineItems) {
            try {
              // Prefer validated display price from the cart item (IQ vs local 10% rule)
              const cartItem = this.orderItems.find(oi =>
                oi.CustPart === lineItem.CustPart ||
                (oi.LineID && lineItem.SourceLineID && oi.LineID === lineItem.SourceLineID)
              );
              const displayTotal = cartItem ? this.getItemDisplayTotal(cartItem) : null;
              const qty = Number(lineItem.Quantity || 1);

              if (displayTotal !== null && displayTotal > 0) {
                await axios.put(`/weborderlineitems/${lineItem.LineID}`, { NetPrice: displayTotal });
                console.log(`Set validated display price for ${lineItem.CustPart} (qty ${qty}): €${displayTotal} (${cartItem?.PriceSource || 'display'})`);
                processedCount++;
              } else {
                const iqUnitPrice = this.getIqPrice(lineItem.CustPart);
                if (iqUnitPrice !== null && !isNaN(iqUnitPrice) && iqUnitPrice > 0) {
                  const total = Number((iqUnitPrice * qty).toFixed(2));
                  await axios.put(`/weborderlineitems/${lineItem.LineID}`, { NetPrice: total });
                  console.log(`Set IQ price for ${lineItem.CustPart} (qty ${qty}): €${total}`);
                  processedCount++;
                } else {
                  console.log(`Calculating component price for ${lineItem.CustPart} (no display/IQ price)`);
                  const costResponse = await axios.get(`/cost-calculation/calculate-item/${lineItem.LineID}`);
                  if (costResponse.data && costResponse.data.success && costResponse.data.data) {
                    const costData = costResponse.data.data;
                    const netPrice = costData.totalCost || 0;
                    console.log(`Calculated cost for ${lineItem.CustPart}: €${netPrice} (NetPrice updated)`);
                    processedCount++;
                  } else {
                    console.warn(`Failed to calculate cost for line item ${lineItem.LineID}:`, costResponse.data);
                    console.log(`Line item ${lineItem.LineID} (${lineItem.CustPart}) needs manual pricing`);
                    processedCount++;
                  }
                }
              }
            } catch (itemError) {
              console.warn(`Error processing price for line item ${lineItem.LineID}:`, itemError.message);
              console.log(`Line item ${lineItem.LineID} (${lineItem.CustPart}) needs manual pricing due to error`);
              processedCount++;
            }
            
            this.setStepProcessing(4, `Processed prices for ${processedCount} of ${createdLineItems.length} items...`);
          }
          
          this.setStepCompleted(4, `Processed prices for ${processedCount} of ${createdLineItems.length} items`);
          
        } catch (priceError) {
          console.warn('Error calculating prices:', priceError);
          this.setStepError(4, 'Price calculation failed - continuing with sales order creation');
        }

        // Step 6: Create IQ Sales Order  
        this.setStepProcessing(5, 'Creating sales order...');
        
        try {
          const salesOrderResponse = await axios.post(`/weborders/${kitId}/create-sales-order`);
          console.log('Sales order creation response:', salesOrderResponse.data);
          
                     if (salesOrderResponse.data.success) {
             this.setStepCompleted(5, 'Sales order created successfully');
           } else if (salesOrderResponse.data.needsManualPricing) {
             this.setStepCompleted(5, `Order saved - ${salesOrderResponse.data.itemsWithoutPrice?.length || 0} items need manual pricing by administrator`);
             console.log('Order saved but needs manual pricing:', salesOrderResponse.data);
           } else {
             this.setStepError(5, 'Order is saved but Sales Order Number could not be generated - Administrator will be notified');
             console.warn('Sales order creation failed but continuing:', salesOrderResponse.data);
           }
         } catch (salesOrderError) {
           console.warn('Sales order creation failed:', salesOrderError);
           this.setStepError(5, 'Order is saved but Sales Order Number could not be generated - Administrator will be notified');
           // Don't fail the entire process for sales order issues
         }

        // Form will be cleared when user closes the dialog

      } catch (error) {
        console.error('Order creation failed:', error);
        this.orderProgress.error = error.response?.data?.message || error.message;
        this.error = 'Failed to create order: ' + (error.response?.data?.message || error.message);
      } finally {
        this.isCreatingOrder = false;
      }
    },

    // Progress tracking methods
    initializeProgress() {
      this.orderProgress = {
        show: true,
        currentStep: 0,
        totalSteps: 6,
        steps: [
          { name: 'Validating User', status: 'pending', message: '' },
          { name: 'Processing Products', status: 'pending', message: '' },
          { name: 'Creating Order', status: 'pending', message: '' },
          { name: 'Adding Line Items', status: 'pending', message: '' },
          { name: 'Calculating Prices', status: 'pending', message: '' },
          { name: 'Creating Sales Order', status: 'pending', message: '' }
        ],
        error: null
      };
    },

    setStepProcessing(stepIndex, message) {
      this.orderProgress.currentStep = stepIndex;
      this.orderProgress.steps[stepIndex].status = 'processing';
      this.orderProgress.steps[stepIndex].message = message;
    },

    setStepCompleted(stepIndex, message) {
      this.orderProgress.steps[stepIndex].status = 'completed';
      this.orderProgress.steps[stepIndex].message = message;
      this.orderProgress.currentStep = stepIndex + 1;
    },

    setStepError(stepIndex, message) {
      this.orderProgress.steps[stepIndex].status = 'error';
      this.orderProgress.steps[stepIndex].message = message;
      // Enable close button when any step has an error
      console.log(`Step ${stepIndex + 1} failed: ${message}`);
    },

    closeProgressModal() {
      this.orderProgress.show = false;
      this.orderProgress.error = null;
      // Clear form data if order was successful
      if (this.orderProgress.currentStep >= this.orderProgress.totalSteps) {
        this.orderItems = [];
        this.orderNumber = '';
        this.error = null;
      }
    },
    
    selectItem(item) {
      this.selectedItem = item;
      this.newItem = {
        kitid: item.KitID,
        custpart: '', // Clear the customer part number as it needs to be new
        length: item.Length,
        cutlength: item.CutLength,
        fittinga: item.FittingA,
        fittingb: item.FittingB,
        orientation: item.Orientation,
        hose: item.Hose,
        quantity: 1,
        vpc: item.VPC || false,
        vpclen: item.VPCLen,
        note: item.Note || '',
        vpcpart: item.VPCPart || '',
        fittinga1: item.FittingA1 || '',
        fittinga2: item.FittingA2 || '',
        fittinga3: item.FittingA3 || '',
        fittinga4: item.FittingA4 || '',
        fittinga5: item.FittingA5 || '',
        fittingb1: item.FittingB1 || '',
        fittingb2: item.FittingB2 || '',
        fittingb3: item.FittingB3 || '',
        fittingb4: item.FittingB4 || '',
        fittingb5: item.FittingB5 || ''
      };
    },
    
    async saveNewItem() {
      this.isSaving = true;
      this.error = null;
      try {
        const newItemData = {
          WebOrderID: this.newItem.kitid,
          CustPart: this.newItem.custpart,
          Length: this.newItem.length,
          CutLength: this.newItem.cutlength,
          FittingA: this.newItem.fittinga,
          FittingB: this.newItem.fittingb,
          Orientation: this.newItem.orientation,
          Hose: this.newItem.hose,
          Quantity: this.newItem.quantity,
          VPC: this.newItem.vpc,
          VPCLen: this.newItem.vpclen,
          Note: this.newItem.note,
          VPCPart: this.newItem.vpcpart,
          FittingA1: this.newItem.fittinga1,
          FittingA2: this.newItem.fittinga2,
          FittingA3: this.newItem.fittinga3,
          FittingA4: this.newItem.fittinga4,
          FittingA5: this.newItem.fittinga5,
          FittingB1: this.newItem.fittingb1,
          FittingB2: this.newItem.fittingb2,
          FittingB3: this.newItem.fittingb3,
          FittingB4: this.newItem.fittingb4,
          FittingB5: this.newItem.fittingb5
        };

        await axios.post('/weborderslineitems', newItemData);
        // Show success message
        this.selectedItem = null;
        this.newItem = {
          kitid: null,
          custpart: '',
          length: null,
          cutlength: null,
          fittinga: '',
          fittingb: '',
          orientation: '',
          hose: '',
          quantity: 1,
          vpc: false,
          vpclen: null,
          note: '',
          vpcpart: '',
          fittinga1: '',
          fittinga2: '',
          fittinga3: '',
          fittinga4: '',
          fittinga5: '',
          fittingb1: '',
          fittingb2: '',
          fittingb3: '',
          fittingb4: '',
          fittingb5: '',
        };
        // Refresh search results
        await this.searchItems();
      } catch (error) {
        console.error('Save failed:', error);
        this.error = 'Failed to save the new item. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },
    
    cancelEdit() {
      this.selectedItem = null;
      this.error = null;
      this.newItem = {
        kitid: null,
        custpart: '',
        length: null,
        cutlength: null,
        fittinga: '',
        fittingb: '',
        orientation: '',
        hose: '',
        flush: false,
        quantity: 1,
        vpc: false,
        item: null,
        vpclen: null,
        note: '',
        sequence: null,
        vpcpart: '',
        stockdate: null,
        stocklevel: 0,
        bin: null,
        BillPart: '',
        fittinga1: '',
        fittinga2: '',
        fittinga3: '',
        fittinga4: '',
        fittinga5: '',
        fittingb1: '',
        fittingb2: '',
        fittingb3: '',
        fittingb4: '',
        fittingb5: '',
      };
    },

    editItemDetails(index) {
      console.log('Opening edit modal for index:', index);
      this.editingItemIndex = index;
      // Get the current item
      const item = this.orderItems[index];
      console.log('Current item before update:', item);
      
      if (item) {
        // Create a new object with all the current values
        const updatedItem = {
          ...item,
          FittingA: item.FittingA || '',
          FittingA1: item.FittingA1 || '',
          FittingA2: item.FittingA2 || '',
          FittingB: item.FittingB || '',
          FittingB1: item.FittingB1 || '',
          FittingB2: item.FittingB2 || '',
          Length: item.Length || 0,
          Orientation: item.Orientation || ''
        };
        
        // Calculate initial cut length
        updatedItem.CutLength = this.calculateCutLength(updatedItem);
        
        console.log('Updated item with default values:', updatedItem);
        
        // Update the item in the array
        this.orderItems.splice(index, 1, updatedItem);
        console.log('Order items after update:', this.orderItems);
      } else {
        console.error('No item found at index:', index);
      }
    },

    closeItemDetails() {
      this.editingItemIndex = null;
    },

    saveItemDetails() {
      // Since we're editing the orderItems array directly,
      // we just need to close the modal
      this.closeItemDetails();
    },

    toggleSearchResults() {
      this.hideSearchResults = !this.hideSearchResults;
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    isOrderItem(item) {
      return this.orderItems.some(orderItem => 
        orderItem.CustPart === item.CustPart && 
        orderItem.KitID === item.KitID
      );
    },

    handleFileUpload(event, fileType) {
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf') {
        if (fileType === 'shippingLabel') {
          this.shippingLabelFile = file;
        } else if (fileType === 'picklist') {
          this.picklistFile = file;
        }
      } else {
        this.error = 'Please select a valid PDF file';
        event.target.value = ''; // Clear the file input
      }
    },

    async fetchAvailableHoses() {
      // Check if already loaded
      if (this.dataLoaded.hoses && this.availableHoses.length > 0) {
        console.log('Hoses already loaded, skipping fetch');
        return;
      }

      try {
        console.log('Fetching available hoses from API...');
        const response = await axios.get('/stock/hoses');

        if (response.data && Array.isArray(response.data)) {
          this.availableHoses = response.data;
          this.dataLoaded.hoses = true;
          console.log(`Loaded ${this.availableHoses.length} hoses`);
        } else {
          console.error('Invalid response format:', response.data);
          this.error = 'Failed to load available hoses: Invalid response format';
        }
      } catch (error) {
        console.error('Error fetching hoses:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
          this.error = `Failed to fetch available hoses: ${error.response.data.message || error.message}`;
        } else {
          this.error = 'Failed to fetch available hoses. Please try again later.';
        }
      }
    },

    startEditing(item) {
      // Set editing mode for this item
      item.editing = true;
    },

    stopEditing(item) {
      // Remove editing mode for this item
      item.editing = false;
    },

    async fetchAvailableAdaptors() {
      // Check if already loaded
      if (this.dataLoaded.adaptors && this.availableAdaptors.length > 0) {
        console.log('Adaptors already loaded, skipping fetch');
        return;
      }

      try {
        console.log('Fetching available adaptors from API...');
        const response = await axios.get('/stock/adaptors');
        
        if (response.data && Array.isArray(response.data)) {
          this.availableAdaptors = response.data;
          this.dataLoaded.adaptors = true;
          console.log(`Loaded ${this.availableAdaptors.length} adaptors`);
        } else {
          console.error('Invalid response format:', response.data);
          this.error = 'Failed to load available adaptors: Invalid response format';
        }
      } catch (error) {
        console.error('Error fetching adaptors:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
          this.error = `Failed to fetch available adaptors: ${error.response.data.message || error.message}`;
        } else {
          this.error = 'Failed to fetch available adaptors. Please try again later.';
        }
      }
    },

    async fetchAvailableCrimps() {
      // Check if already loaded
      if (this.dataLoaded.crimps && this.availableCrimps.length > 0) {
        console.log('Crimps already loaded, skipping fetch');
        return;
      }

      try {
        console.log('Fetching available crimps from API...');
        const response = await axios.get('/stock/crimps');
        
        if (response.data && Array.isArray(response.data)) {
          this.availableCrimps = response.data;
          this.dataLoaded.crimps = true;
          console.log(`Loaded ${this.availableCrimps.length} crimps`);
        } else {
          console.error('Invalid response format:', response.data);
          this.error = 'Failed to load available crimps: Invalid response format';
        }
      } catch (error) {
        console.error('Error fetching crimps:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
          this.error = `Failed to fetch available crimps: ${error.response.data.message || error.message}`;
        } else {
          this.error = 'Failed to fetch available crimps. Please try again later.';
        }
      }
    },


    async calculateItemCost(index) {
      const item = this.orderItems[index];
      this.showCostModal = true;
      this.selectedItemCost = null;
      this.costModalPart = item?.CustPart || item?.BillPart || null;
      this.costModalQty = item?.Quantity || 1;

      try {
        // Get label values from the original kit if available
        const labelValue = item.KitDetails?.Label ?? false;
        const lblDoubles = item.KitDetails?.LblDoubles ?? "2000";

        console.log('Using label values from kit:', {
          Label: labelValue,
          LblDoubles: lblDoubles,
          KitDetails: item.KitDetails
        });

        // Get customer - for admin use selectedCustomer, otherwise use user customer
        const customerValue = this.isAdminOrManager ? this.selectedCustomer : this.$store.state.user?.customer;

        console.log('Using customer for cost calculation:', {
          Customer: customerValue,
          IsAdminOrManager: this.isAdminOrManager,
          SelectedCustomer: this.selectedCustomer,
          UserCustomer: this.$store.state.user?.customer
        });

        const vpcLen = Number(item.VPCLen);
        const hasVpcData = !!item.VPCPart && !isNaN(vpcLen) && vpcLen > 0;

        const response = await axios.post('/cost-calculation/calculate-item', {
          CustPart: item.CustPart,
          Hose: item.Hose,
          CutLength: item.CutLength,
          VPCPart: hasVpcData ? item.VPCPart : null,
          VPCLen: hasVpcData ? item.VPCLen : null,
          FittingA: item.FittingA,
          FittingB: item.FittingB,
          FittingA1: item.FittingA1,
          FittingA2: item.FittingA2,
          FittingA3: item.FittingA3,
          FittingA4: item.FittingA4,
          FittingA5: item.FittingA5,
          FittingB1: item.FittingB1,
          FittingB2: item.FittingB2,
          FittingB3: item.FittingB3,
          FittingB4: item.FittingB4,
          FittingB5: item.FittingB5,
          Label: labelValue,
          LblDoubles: lblDoubles,
          Quantity: item.Quantity,
          customer: customerValue
        });

        if (response.data && response.data.success) {
          this.selectedItemCost = response.data.data;
        } else {
          console.error('Invalid cost calculation response:', response.data);
          this.error = 'Failed to calculate cost';
        }
      } catch (error) {
        console.error('Error calculating cost:', error);
        this.error = 'Failed to calculate cost: ' + (error.response?.data?.message || error.message);
      }
    },

    async calculateItemCostFromSearch(item) {
      this.showCostModal = true;
      this.selectedItemCost = null;
      this.costModalPart = item?.CustPart || item?.BillPart || null;
      this.costModalQty = item?.Quantity || 1;

      try {
        // Calculate cut length first if not available
        let cutLength = item.CutLength;
        if (!cutLength && item.Length) {
          try {
            cutLength = this.calculateCutLength(item);
          } catch (error) {
            console.warn('Could not calculate cut length, using original length:', error.message);
            cutLength = item.Length;
          }
        }

        // Get label values from the original kit if available, or fetch them
        let labelValue = item.KitDetails?.Label ?? false;
        let lblDoubles = item.KitDetails?.LblDoubles ?? "2000";
        
        // If we don't have kit details but have a KitID, fetch them
        if (!item.KitDetails && item.KitID) {
          console.log('Fetching kit details for cost calculation, KitID:', item.KitID);
          try {
            let url = '/kits';
            const params = new URLSearchParams();
            params.append('kitid', item.KitID);
            if (this.isAdminOrManager && this.selectedCustomer) {
              params.append('customer', this.selectedCustomer);
            }
            
            if (params.toString()) {
              url += `?${params.toString()}`;
            }

            const kitResponse = await axios.get(url);
            
            if (kitResponse.data && kitResponse.data.data && kitResponse.data.data.length > 0) {
              const kit = kitResponse.data.data[0];
              labelValue = kit.Label ?? false;
              lblDoubles = kit.LblDoubles ?? "2000";
              console.log('Fetched kit label details:', { Label: labelValue, LblDoubles: lblDoubles });
            } else {
              console.log('No kit details found for cost calculation, using defaults');
            }
          } catch (error) {
            console.warn('Error fetching kit details for cost calculation:', error);
            // Keep defaults
          }
        }

        // Get customer - for admin use selectedCustomer, otherwise use user customer
        const customerValue = this.isAdminOrManager ? this.selectedCustomer : this.$store.state.user?.customer;

        console.log('Calculating cost for search result item:', {
          CustPart: item.CustPart,
          Hose: item.Hose,
          CutLength: cutLength,
          Label: labelValue,
          LblDoubles: lblDoubles,
          Customer: customerValue,
          IsAdminOrManager: this.isAdminOrManager,
          SelectedCustomer: this.selectedCustomer,
          UserCustomer: this.$store.state.user?.customer
        });

        const vpcLen = Number(item.VPCLen);
        const hasVpcData = !!item.VPCPart && !isNaN(vpcLen) && vpcLen > 0;

        const response = await axios.post('/cost-calculation/calculate-item', {
          CustPart: item.CustPart,
          Hose: item.Hose,
          CutLength: cutLength,
          VPCPart: hasVpcData ? item.VPCPart : null,
          VPCLen: hasVpcData ? item.VPCLen : null,
          FittingA: item.FittingA,
          FittingB: item.FittingB,
          FittingA1: item.FittingA1,
          FittingA2: item.FittingA2,
          FittingA3: item.FittingA3,
          FittingA4: item.FittingA4,
          FittingA5: item.FittingA5,
          FittingB1: item.FittingB1,
          FittingB2: item.FittingB2,
          FittingB3: item.FittingB3,
          FittingB4: item.FittingB4,
          FittingB5: item.FittingB5,
          Label: labelValue,
          LblDoubles: lblDoubles,
          Quantity: item.Quantity || 1,
          customer: customerValue
        });

        if (response.data && response.data.success) {
          this.selectedItemCost = response.data.data;
        } else {
          console.error('Invalid cost calculation response:', response.data);
          this.error = 'Failed to calculate cost';
        }
      } catch (error) {
        console.error('Error calculating cost for search result:', error);
        this.error = 'Failed to calculate cost: ' + (error.response?.data?.message || error.message);
      }
    },
  },
  computed: {
    isAdminOrManager() {
      return this.userRole === 'Admin' || this.userRole === 'Manager';
    },
    hasStepErrors() {
      return this.orderProgress.steps.some(step => step.status === 'error');
    },
    calculateCutLength() {
      return (item) => {
        console.log('Starting cut length calculation for item:', item);
        if (!item || !item.Length) {
          console.log('No item or length provided, returning 0');
          return 0;
        }
        
        // Get the crimp fitting lengths from the available crimps
        const fittingA = this.availableCrimps.find(crimp => crimp.PartNo === item.FittingA);
        const fittingB = this.availableCrimps.find(crimp => crimp.PartNo === item.FittingB);
        
        console.log('Found crimp fittings:', {
          fittingA: fittingA ? { PartNo: fittingA.PartNo, Length: fittingA.Length } : null,
          fittingB: fittingB ? { PartNo: fittingB.PartNo, Length: fittingB.Length } : null
        });
        
        // Get the adaptor lengths from the available adaptors
        const fittingA2 = this.availableAdaptors.find(adaptor => adaptor.PartNo === item.FittingA2);
        const fittingB2 = this.availableAdaptors.find(adaptor => adaptor.PartNo === item.FittingB2);
        
        console.log('Available adaptors:', this.availableAdaptors);
        console.log('Looking for adaptors:', {
          fittingA2: item.FittingA2,
          fittingB2: item.FittingB2
        });
        console.log('Found adaptor fittings:', {
          fittingA2: fittingA2 ? { PartNo: fittingA2.PartNo, Length: fittingA2.Length } : null,
          fittingB2: fittingB2 ? { PartNo: fittingB2.PartNo, Length: fittingB2.Length } : null
        });
        
        // Check if fittings are found and have lengths
        if (!fittingA || !fittingA.Length) {
          console.error(`Length not found for fitting A: ${item.FittingA}`);
          throw new Error(`Length not found for fitting A: ${item.FittingA}`);
        }
        if (!fittingB || !fittingB.Length) {
          console.error(`Length not found for fitting B: ${item.FittingB}`);
          throw new Error(`Length not found for fitting B: ${item.FittingB}`);
        }
        
        // Get adaptor lengths (default to 0 if not found or length is null/undefined)
        const fittingA2Length = fittingA2 && fittingA2.Length !== null && fittingA2.Length !== undefined ? fittingA2.Length : 0;
        const fittingB2Length = fittingB2 && fittingB2.Length !== null && fittingB2.Length !== undefined ? fittingB2.Length : 0;
        
        console.log('Using adaptor lengths:', {
          fittingA2Length,
          fittingB2Length,
          fittingA2: fittingA2 ? fittingA2 : 'not found',
          fittingB2: fittingB2 ? fittingB2 : 'not found'
        });
        
        // Calculate total fitting length (crimps + adaptors)
        const totalFittingLength = fittingA.Length + fittingB.Length + fittingA2Length + fittingB2Length;
        console.log('Total fitting length calculation:', {
          crimpA: fittingA.Length,
          crimpB: fittingB.Length,
          adaptorA2: fittingA2Length,
          adaptorB2: fittingB2Length,
          total: totalFittingLength
        });
        
        // Calculate cut length (overall length minus fitting lengths)
        const cutLength = Math.max(0, item.Length - totalFittingLength);
        console.log('Final cut length calculation:', {
          overallLength: item.Length,
          totalFittingLength,
          cutLength
        });
        
        return cutLength;
      };
    }
  },
  created() {
    // Add getWeek method to Date prototype if not exists
    if (!Date.prototype.getWeek) {
      Date.prototype.getWeek = function() {
        const date = new Date(this.getTime());
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
        const week1 = new Date(date.getFullYear(), 0, 4);
        return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
      };
    }
    // Data will be fetched in mounted() after authentication
  },
  mounted() {
    // Get the user's role from the auth check endpoint
    const checkAuth = async () => {
      try {
        const response = await axios.get('/auth/check-auth');
        this.userRole = response.data.user.role;
        if (this.isAdminOrManager) {
          await this.fetchCustomers();
        }
        // Fetch all required data after authentication
        await Promise.all([
          this.fetchAvailableHoses(),
          this.fetchAvailableAdaptors(),
          this.fetchAvailableCrimps()
        ]);
      } catch (error) {
        console.error('Error checking auth:', error);
        // Don't try to fetch other data if auth fails
        this.error = 'Authentication error. Please log in again.';
        this.$router.push('/login');
      }
    };

    checkAuth();
  },
  watch: {},
};
</script>

<style scoped>
.line-item-container {
  padding: 1rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
}

.section-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
  padding-left: 0;
}

.section-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  margin-top: 2rem;
  padding-left: 0;
}

.search-section {
  background-color: var(--background);
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.search-form {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
}

.form-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.form-input {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;
  background-color: white;
  height: 38px;
}

.customer-select {
  width: 100%;
  min-width: 200px;
  background-color: white;
  height: 38px;
}

.search-button {
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
  white-space: nowrap;
  height: 38px;
  min-width: 100px;
}

.search-button:hover {
  background-color: var(--primary-color-dark);
}

.search-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.results-section {
  background-color: var(--background);
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.results-table {
  margin-top: 1rem;
  overflow-x: auto;
  margin-left: 0;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-left: 0;
}

th, td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th:first-child, td:first-child {
  padding-left: 0.5rem;
}

th {
  background-color: var(--background-alt);
  font-weight: 600;
  color: var(--text-primary);
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-primary:hover {
  background-color: var(--primary-hover);
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.error-message {
  color: #dc2626;
  background-color: #fee2e2;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.loading-message {
  color: #4b5563;
  text-align: center;
  padding: 1rem;
}

.no-results {
  color: #6b7280;
  text-align: center;
  padding: 1rem;
}

@media (max-width: 768px) {
  .search-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .section-title {
    text-align: center;
  }

  .search-form {
    flex-direction: column;
    align-items: stretch;
  }

  .form-group {
    min-width: 100%;
  }

  .customer-select {
    width: 100%;
  }
}

select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

.hose-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.edit-btn:hover {
  color: #333;
  background-color: #f0f0f0;
}

.edit-btn i {
  font-size: 16px;
}

.order-actions {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 400px;
}

.order-info .form-group {
  min-width: 300px;
}

.order-info .form-input {
  width: 300px;
}

.create-order-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-self: flex-start;
  min-width: 150px;
}

.create-order-button:hover {
  background-color: var(--primary-hover);
}

.create-order-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.table-input {
  width: 80px;
  padding: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
}

.quantity-input {
  width: 60px;
  padding: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
}

.fitting-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.edit-details-button {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.edit-details-button:hover {
  color: var(--primary-hover);
  text-decoration: underline;
}

.remove-button {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.remove-button:hover {
  color: #b91c1c;
  text-decoration: underline;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
}

.close-button:hover {
  color: #333;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  background-color: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.form-section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  flex: 1;
  min-width: 200px;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-button {
  background-color: #f3f4f6;
  color: #4b5563;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background-color: #e5e7eb;
}

.save-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.save-button:hover {
  background-color: var(--primary-hover);
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    padding: 1rem;
  }

  .form-row {
    flex-direction: column;
  }

  .form-group {
    width: 100%;
  }
}

.order-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.toggle-search-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-self: flex-start;
}

.toggle-search-button:hover {
  background-color: var(--primary-hover);
}

.calculate-price-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-right: 0.5rem;
}

.calculate-price-button:hover {
  background-color: var(--primary-hover);
}

/* Action buttons container for search results */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Money icon button styles */
.btn-icon {
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 32px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background-color: #218838;
}

.btn-icon:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.cost-modal {
  max-width: 500px;
  padding: 0;
  overflow: hidden;
}

.cost-modal .modal-header {
  background-color: var(--primary-color);
  color: white;
  padding: 1.25rem 1.5rem;
  margin: 0;
}

.cost-modal .modal-header h3 {
  color: white;
  margin: 0;
  font-size: 1.5rem;
}

.cost-modal .close-button {
  color: white;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

/* Add spacing between Qty label and close button in modal header */
.modal-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* adjust spacing as needed */
}

.cost-modal .close-button:hover {
  opacity: 1;
}

.cost-modal .modal-body {
  padding: 1.5rem;
}

.cost-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cost-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: var(--background-alt);
  border-radius: 0.5rem;
  transition: transform 0.2s ease;
}

.cost-item:hover {
  transform: translateX(4px);
}

.cost-item .label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 1rem;
}

.cost-item .value {
  font-weight: 600;
  color: var(--primary-color);
  font-size: 1.1rem;
}

.cost-total {
  margin-top: 1rem;
}

.total-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 1rem 0;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: var(--primary-color);
  border-radius: 0.5rem;
  color: white;
}

.total-label {
  font-size: 1.25rem;
  font-weight: 600;
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.loading-cost {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--background-alt);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Progress Modal Styles */
.progress-modal {
  max-width: 600px;
  padding: 0;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.progress-header {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: white;
  padding: 1.5rem 2rem;
}

.progress-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-title {
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.progress-close-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.progress-close-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.progress-close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: white;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.progress-steps {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.progress-step {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.progress-step.pending {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
}

.progress-step.processing {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  transform: scale(1.02);
}

.progress-step.completed {
  background-color: #d1fae5;
  border: 1px solid #10b981;
}

.progress-step.error {
  background-color: #fee2e2;
  border: 1px solid #ef4444;
}

.step-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
}

.progress-step.pending .step-icon {
  background-color: #e5e7eb;
  color: #6b7280;
}

.progress-step.processing .step-icon {
  background-color: #f59e0b;
  color: white;
}

.progress-step.completed .step-icon {
  background-color: #10b981;
  color: white;
}

.progress-step.error .step-icon {
  background-color: #ef4444;
  color: white;
}

.icon-spinner {
  animation: spin 1s linear infinite;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.step-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.step-message {
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
}

.progress-error {
  padding: 2rem;
  border-top: 1px solid #e5e7eb;
  background-color: #fef2f2;
  text-align: center;
}

.progress-error h4 {
  color: #dc2626;
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
}

.progress-error p {
  color: #7f1d1d;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

.progress-success {
  padding: 2rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f0fdf4;
  text-align: center;
}

.success-icon {
  width: 64px;
  height: 64px;
  background-color: #10b981;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 1rem auto;
  animation: successPulse 0.6s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.progress-success h4 {
  color: #16a34a;
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
}

.progress-success p {
  color: #15803d;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

.progress-partial-error {
  padding: 2rem;
  border-top: 1px solid #e5e7eb;
  background-color: #fffbeb;
  text-align: center;
}

.progress-partial-error h4 {
  color: #d97706;
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
}

.progress-partial-error p {
  color: #92400e;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}
</style> 